import Logger, {LOG_LEVELS} from '../utils/logger';
import JSON_TRANSFORM from '../utils/jsonTransform/jsonTransformers';

export const MODULE_NAME = 'MANAGED_UX_FORMS';
const log = new Logger(MODULE_NAME, LOG_LEVELS.DEBUG);

function namespaced(name) {
  return MODULE_NAME+'.'+name;
}

const INITIALISE_FORM = namespaced('INITIALISE_FORM');
const FORM_FIELD_CHANGE = namespaced('FORM_FIELD_CHANGE');
const FORM_BULK_CHANGE = namespaced('FORM_BULK_CHANGE');
const SUBMIT_FORM = namespaced('SUBMIT_FORM');
const RESET_FORM_FIELDS_TO_INITIAL_VALUES = namespaced('RESET_FORM_FIELDS_TO_INITIAL_VALUES');
const CLEAR_FORM_FIELDS = namespaced('CLEAR_FORM_FIELDS');
const REMOVE_FORM = namespaced('REMOVE_FORM');
const ADD_STRUCTURED_OBJECT = namespaced('ADD_STRUCTURED_OBJECT');
const STRUCTURED_FIELD_CHANGE = namespaced('STRUCTURED_FIELD_CHANGE');

export const ACTION_TYPES = {
  INITIALISE_FORM: INITIALISE_FORM,
  RESET_FORM_FIELDS_TO_INITIAL_VALUES,
  FORM_FIELD_CHANGE,
  FORM_BULK_CHANGE,
  CLEAR_FORM_FIELDS,
  SUBMIT_FORM,
  ADD_STRUCTURED_OBJECT,
  STRUCTURED_FIELD_CHANGE,
  REMOVE_FORM
};

// In redux parlance, this is a reducer
export const updateSubStateFromAction = (state = {forms: {}}, action) => {
  log.debug('updateSubStateFromAction: ', action, state );
  const getNewState = (action, state) => {
    const formName = action.formName;
    const fieldName = action.fieldName;
    const value = action.value;

    switch (action.type) {
      case INITIALISE_FORM:
        const initialValues = action.initialValues;
        const fieldValues = JSON.parse(JSON.stringify(action.initialValues));
        if (!state.forms) {
          state.forms = {};
        }
        state.forms[action.formName] = { formName: action.formName, initialValues, fieldValues, submitted: false, changed: false };
        return state;
      case SUBMIT_FORM:
        if (!state.forms) {
          state.forms = {};
        }
        if (!state.forms[action.formName]) {
          state.forms[action.formName]= {formName: action.formName, initialValues: {}, fieldValues: {}, submitted: false, changed: false};
        }
        state.forms[action.formName].submitted = true;
        state.forms[action.formName].changed = false;
        return state;
      case ADD_STRUCTURED_OBJECT:
        if (!state.forms){
          state.forms = {};
        }
        if (!state.forms[action.formName]){
          state.forms[action.formName] = { formName: action.formName, initialValues: {}, fieldValues: {}, structured: {}, submitted: false, changed: false};
        }
        state.forms[action.formName].structured = action.structured;
        return state;
      case RESET_FORM_FIELDS_TO_INITIAL_VALUES:
        if (!state.forms) {
          state.forms = {};
        }
        if (!state.forms[action.formName]) {
          state.forms[action.formName]= {formName: action.formName, initialValues: {}, fieldValues: {}, submitted: false, changed: false};
        }
        state.forms[action.formName].fieldValues = JSON.parse(JSON.stringify(state.forms[action.formName].initialValues));
        state.forms[action.formName].submitted = false;
        state.forms[action.formName].changed = false;
        return state;
      case REMOVE_FORM:
        if (!state.forms) {
          state.forms = {};
        }
        delete state.forms[action.formName];
        return state;
      case CLEAR_FORM_FIELDS:
        if (!state.forms) {
          state.forms = {};
        }
        if (!state.forms[action.formName]) {
          state.forms[action.formName]= {formName: action.formName, initialValues: {}, fieldValues: {}};
        }
        state.forms[action.formName].fieldValues = {};
        state.forms[action.formName].submitted = false;
        state.forms[action.formName].changed = true;
        return state;
      case STRUCTURED_FIELD_CHANGE:
        if (!state.forms) {
          state.forms = {};
        }
        if (!state.forms[action.formName]) {
          state.forms[action.formName]= {formName: action.formName, initialValues: {}, fieldValues: {}};
        }
        state.forms[formName].fieldValues[fieldName] = value;
        state.forms[formName].structured =  JSON_TRANSFORM.unflattenJSON(state.forms[formName].fieldValues);
        state.forms[action.formName].submitted = false;
        state.forms[action.formName].changed = true;
        return state;
      case FORM_FIELD_CHANGE:
        if (!state.forms) {
          state.forms = {};
        }
        if (!state.forms[formName]) {
          state.forms[formName]= {formName, initialValues: {}, fieldValues: {}};
        }
        state.forms[formName].fieldValues[fieldName] = value;
        state.forms[action.formName].submitted = false;
        state.forms[action.formName].changed = true;
        return state;
      case FORM_BULK_CHANGE:
        if (!state.forms) {
          state.forms = {};
        }
        if (!state.forms[formName]) {
          state.forms[formName]= {formName, initialValues: {}, fieldValues: {}};
        }
        state.forms[action.formName].fieldValues =  Object.assign({}, state.forms[action.formName].fieldValues, value);
        state.forms[action.formName].submitted = false;
        state.forms[action.formName].changed = true;
        return state;
      default:
        return state;
    }
  };
  const newState = getNewState(action, JSON.parse(JSON.stringify(state)));
  log.debug('updateSubStateFromAction:  newState', newState );
  return newState;
};

function validateFieldName(formName, fieldName, methodName) {
  if (typeof fieldName !== 'string') {
    log.error('%s(%s): formName must be a string not ', methodName, formName, typeof fieldName);
    throw new Error(methodName+'('+formName+'): fieldName must be a string');
  }
  if (fieldName === '') {
    log.error('%s(%s): fieldName must not be blank', methodName, formName);
    throw new Error(methodName+'('+formName+'): fieldName must not be blank');
  }
}

function validateFormName(formName, methodName) {
  if (typeof formName !== 'string') {
    log.error('%s(): formName must be a string not ', methodName, typeof formName);
    throw new Error(methodName+': formName must be a string');
  }
  if (formName === '') {
    log.error('%s(): formName must not be blank', methodName);
    throw new Error(methodName+': formName must not be blank');
  }
}

export const API = {
  doAddStructuredObject: (formName, structured) => {
    log.debug('doAddStructuredObject(): ', formName, structured);
    structured = structured || {};
    return (dispatch, getState) => {
      dispatch({type: ADD_STRUCTURED_OBJECT, formName, structured});
    }
  },
  doInitialiseForm: (formName, initialValues) => {
    log.debug('doInitialiseForm(): ', formName, initialValues);
    initialValues = initialValues || {};
    validateFormName(formName, 'doInitialiseForm');

    if (typeof initialValues !== 'object') {
      log.error('doInitialiseForm(): initialValues must be an object not ', typeof initialValues);
      throw new Error('doInitialiseForm: formName must be an object');
    }
    return (dispatch, getState) => {
      log.debug('doInitialiseForm(%s): dispatching', formName);
      dispatch({type: INITIALISE_FORM, formName, initialValues});
    }
  },
  doResetFormFieldsToInitialValues: (formName) => {
    log.debug('doResetFormFieldsToInitialValues(): ', formName);
    validateFormName(formName, 'doResetFormFieldsToInitialValues');

    return (dispatch, getState) => {
      log.debug('doResetFormFieldsToInitialValues(%s): dispatching', formName);
      dispatch({type: RESET_FORM_FIELDS_TO_INITIAL_VALUES, formName});
    }
  },
  doClearFormFields: (formName) => {
    log.debug('doClearFormFields(): ', formName);
    validateFormName(formName, 'doClearFormFields');

    return (dispatch, getState) => {
      log.debug('doClearFormFields(%s): dispatching', formName);
      dispatch({type: CLEAR_FORM_FIELDS, formName});
    }
  },
  doFormSubmitted: (formName, fieldValues) => {
    log.debug('doFormSubmitted(): ', formName);
    validateFormName(formName, 'doSubmitForm');

    return (dispatch, getState) => {
      log.debug('doFormSubmitted(%s): dispatching', formName);
      dispatch({type: SUBMIT_FORM, formName});
    }
  },
  doRemoveForm: (formName) => {
    log.debug('doRemoveForm(): ', formName);
    validateFormName(formName, 'doRemoveForm');

    return (dispatch, getState) => {
      log.debug('doRemoveForm(%s): dispatching', formName);
      dispatch({type: REMOVE_FORM, formName});
    }
  },
  doStructuredFieldChange: (formName, fieldName, value) => {
    log.debug('doStructuredFieldChange(): ', formName, fieldName, value);
    validateFormName(formName, 'doStructuredFieldChange');
    validateFieldName(formName, fieldName, 'doStructuredFieldChange');

    return (dispatch, getState) => {
      log.debug('doStructuredFieldChange(%s, %s): dispatching ', formName, fieldName, value);
      dispatch({type: STRUCTURED_FIELD_CHANGE, formName, fieldName, value});
    }
  },
  doFormFieldChange: (formName, fieldName, value) => {
    log.debug('doFormFieldChange(): ', formName, fieldName, value);
    validateFormName(formName, 'doFormFieldChange');
    validateFieldName(formName, fieldName, 'doFormFieldChange');

    return (dispatch, getState) => {
      log.debug('doFormFieldChange(%s, %s): dispatching ', formName, fieldName, value);
      dispatch({type: FORM_FIELD_CHANGE, formName, fieldName, value});
    }
  },
  doFormBulkChange: (formName, value) => {
    log.debug('doFormBulkChange(): ', formName, value);
    validateFormName(formName, 'doFormBulkChange');
    if(value) {
       Object.keys(value).forEach((fieldName) => validateFieldName(formName, fieldName, 'doFormBulkChange'))
    } 
    return (dispatch, getState) => {
      log.debug('doFormBulkChange(%s, %s): dispatching ', formName, value);
      dispatch({type: FORM_BULK_CHANGE, formName, value});
    };
  },
  flattenJSON: (data) => {
    // Deprecated use JSON_TRANSFORM.flattenJSON()
    let result = {};
    function recurse (cur, prop) {
      if (Object(cur) !== cur) {
        result[prop] = cur;
      } else if (Array.isArray(cur)) {
        for(var i=0, l=cur.length; i<l; i++)
          recurse(cur[i], prop ? prop+"."+i : ""+i);
        if (l == 0)
          result[prop] = [];
      } else {
        var isEmpty = true;
        for (var p in cur) {
          isEmpty = false;
          recurse(cur[p], prop ? prop+"."+p : p);
        }
        if (isEmpty)
          result[prop] = {};
      }
    }
    recurse(data, "");
    return result;
  },
  unflattenJSON: (data) => {
    // Deprecated use JSON_TRANSFORM.unflattenJSON()
    "use strict";
    if (Object(data) !== data || Array.isArray(data))
      return data;
    const regex = /\.?([^.\[\]]+)|\[(\d+)\]/g;
    let resultholder = {};
    for (let p in data) {
      let cur = resultholder;
      let prop = "";
      let m;
      while (m = regex.exec(p)) {
        cur = cur[prop] || (cur[prop] = (m[2] ? [] : {}));
        prop = m[2] || m[1];
      }
      cur[prop] = data[p];
    }
    return resultholder[""] || resultholder;
  }
};


export default API;

